import type { PayloadAction } from '@reduxjs/toolkit';
import {
  type ActivityItem,
  type ActivityLogState,
  initialState,
  isSingleActivityItem,
  type SingleActivityItem,
} from './types';

/**
 * When Pusher adds a single log whose ID already exists in a grouped log's entries,
 * remove that ID from the group so we don't show duplicates (single has priority).
 * If a group becomes empty after removal, it is deleted from source and logIds.
 */
function removeIdFromGroupedEntries(state: ActivityLogState, idToRemove: string): void {
  Object.entries(state.source).forEach(([logId, item]) => {
    if (item?.group_type === 'multiple' && item?.attributes?.entries?.includes(idToRemove)) {
      item.attributes.entries = item.attributes.entries.filter((entryId) => entryId !== idToRemove);

      // Remove the group entirely if no entries remain
      if (item.attributes.entries.length === 0) {
        delete state.source[logId];
        const idx = state.logIds.indexOf(logId);
        if (idx !== -1) {
          state.logIds.splice(idx, 1);
        }
      }
    }
  });
}

const reducers = {
  setCurrentSrOrWo: (
    state: ActivityLogState,
    action: PayloadAction<{
      id: string;
      page: 'work-orders' | 'service-requests';
    }>,
  ) => {
    // Clear if switching to different work order/service request
    if (state.currentSrOrWoId !== action.payload.id) {
      state.source = {};
      state.logIds = [];
      state.currentSrOrWoId = action.payload.id;
      state.meta = initialState.meta;
      state.expandingGroups = {};
      state.currentPage = action.payload.page;
      // Clear any draft typed against the previous entity so it can never be
      // submitted against this one (APX-557).
      state.draftedInternalNote = initialState.draftedInternalNote;
      state.summary = initialState.summary;
      state.isFetchingSummary = initialState.isFetchingSummary;
      state.hasSummaryApiFailed = initialState.hasSummaryApiFailed;
    }
  },
  addActivityLog: (
    state: ActivityLogState,
    action: PayloadAction<{
      activity: SingleActivityItem;
    }>,
  ) => {
    const { activity } = action.payload;

    if (!activity.activity_log_id) return;

    const id = activity.activity_log_id;

    // Update the source map
    state.source[id] = activity;

    // Single has priority: remove this id from any group that contains it
    removeIdFromGroupedEntries(state, id);

    // If ID already exists in logIds, exit early
    if (state.logIds.indexOf(id) !== -1) {
      return;
    }

    // If ID is new, add to the top
    state.logIds.unshift(id);
  },

  deleteActivityLog: (
    state: ActivityLogState,
    action: PayloadAction<{ activityLogId: string }>,
  ) => {
    const { activityLogId } = action.payload;

    delete state.source?.[activityLogId];
    const index = state.logIds.indexOf(activityLogId);
    if (index !== -1) {
      state.logIds.splice(index, 1);
    }

    if (state.draftedInternalNote.logId === activityLogId) {
      state.draftedInternalNote = {
        note: '',
      };
    }
  },

  // Partially update an existing activity log entry.
  // Handles the special case where the backend assigns a new ID
  // (e.g. optimistic → server-confirmed ID swap).
  patchActivityLog: (
    state: ActivityLogState,
    action: PayloadAction<{
      activityLogId: string;
      updatedLogData: Partial<ActivityItem>;
    }>,
  ) => {
    const { activityLogId, updatedLogData } = action.payload;
    const existing = state.source[activityLogId];

    if (!existing) return;

    // Merge updated fields into the existing activity
    const updated = { ...existing, ...updatedLogData } as ActivityItem;

    const newId = updatedLogData.activity_log_id;
    const idChanged = newId && newId !== activityLogId;

    if (idChanged) {
      // Store under the new key and remove the old one
      state.source[newId] = updated;
      delete state.source[activityLogId];

      // Swap the ID in logIds to preserve display order
      const index = state.logIds.indexOf(activityLogId);
      if (index !== -1) {
        state.logIds[index] = newId;
      }
    } else {
      state.source[activityLogId] = updated;
    }
  },

  updateActivityLogAttributes: (
    state: ActivityLogState,
    action: PayloadAction<{
      activityLogId: string;
      updatedAttributes: Partial<SingleActivityItem['attributes']>;
    }>,
  ) => {
    const { activityLogId, updatedAttributes } = action.payload;
    const activityLog = state.source?.[activityLogId];

    // ✅ Narrow to single items only
    if (isSingleActivityItem(activityLog)) {
      state.source[activityLogId] = {
        ...activityLog,
        attributes: {
          ...activityLog.attributes,
          ...updatedAttributes,
        },
      };
    }
  },

  clearActivityLog: () => initialState,

  // ----- GROUP EXPANSION -----
  setGroupExpansionState: (
    state: ActivityLogState,
    action: PayloadAction<{ activityLogId: string; expand: boolean }>,
  ) => {
    const { activityLogId, expand } = action.payload;
    state.expandingGroups[activityLogId] = expand;
  },

  addExpandedGroupActivities: (
    state: ActivityLogState,
    action: PayloadAction<{
      expandedActivities: SingleActivityItem[];
    }>,
  ) => {
    const { expandedActivities } = action.payload;

    // Add expanded activities to source
    expandedActivities.forEach((activity) => {
      state.source[activity?.activity_log_id] = activity;
    });
  },

  // ----- DRAFT MANAGEMENT -----
  saveDraft: (
    state: ActivityLogState,
    action: PayloadAction<{
      note: string;
      logId?: string;
      noteId?: string;
      serviceRequestId?: string;
    }>,
  ) => {
    if (!state.currentSrOrWoId) return;
    // Entity-bound guard: if the caller names the entity it drafted against,
    // drop the write when the current entity has changed — otherwise a failed
    // POST for entity A would re-bind A's text under B after a switch (APX-557).
    const { serviceRequestId, ...draft } = action.payload;
    if (serviceRequestId && serviceRequestId !== state.currentSrOrWoId) return;
    state.draftedInternalNote = {
      ...draft,
    };
  },

  clearDraft: (state: ActivityLogState) => {
    state.draftedInternalNote = {
      note: '',
    };
  },

  // ----- ORIGINAL NOTE MANAGEMENT -----
  updateOriginalNote: (state: ActivityLogState, action: PayloadAction<string>) => {
    state.originalNote = action.payload;
  },

  setAiSummaryOutdated: (
    state: ActivityLogState,
    action: PayloadAction<{ serviceRequestId: string; isOutdated: boolean }>,
  ) => {
    const { serviceRequestId, isOutdated } = action.payload;

    // Entity fence: ignore events for a service request the slice is not currently holding.
    if (state.currentSrOrWoId !== serviceRequestId || state.currentPage !== 'service-requests') {
      return;
    }

    if (Object.keys(state.summary).length > 0) {
      state.summary.is_ai_summary_outdated = isOutdated;
    }
  },
};
export default reducers;
